/**
 * Engine de reprodução.
 *
 * Preview em tempo real não pode usar FFmpeg: o round-trip mataria a edição. A
 * composição acontece no navegador, e a paridade com o render vem de consumir a MESMA
 * timeline e as MESMAS funções de interpolação de `@easyedit/timeline`.
 *
 * O relógio é o do `AudioContext`, não o `requestAnimationFrame`. rAF engasga quando a
 * aba perde foco ou o compositor atrasa; o relógio de áudio é monotônico. O vídeo segue
 * o áudio, nunca o contrário — é assim que se evita deriva audível.
 */
import {
  activeItemsAt,
  evaluateItem,
  fadeGain,
  layoutVisual,
  sequenceDuration,
  sourceTimeAt,
  type Sequence,
  type TimelineItem,
  type Track,
} from '@easyedit/timeline'
import { MediaPool, type MediaSource } from './mediaPool'
import { drawCaption, drawText } from './textRenderer'

export interface EngineAssets {
  get(assetId: string): MediaSource | undefined
}

export interface EngineCallbacks {
  onTimeUpdate: (time: number) => void
  onEnded: () => void
  onError?: (message: string) => void
}

/** Acima disso, ressincroniza com um seek; abaixo, deixa o vídeo correr. */
const DRIFT_TOLERANCE = 0.08

export class PlaybackEngine {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private audioContext: AudioContext
  private masterGain: GainNode
  private pool: MediaPool

  private getSequence: () => Sequence | null
  private assets: EngineAssets
  private callbacks: EngineCallbacks

  private playing = false
  private currentTime = 0
  /** `audioContext.currentTime` no instante em que o play começou. */
  private clockStart = 0
  private clockOffset = 0
  private rafId: number | null = null
  private lastReported = -1

  constructor(
    canvas: HTMLCanvasElement,
    getSequence: () => Sequence | null,
    assets: EngineAssets,
    callbacks: EngineCallbacks,
  ) {
    this.canvas = canvas
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('Canvas 2D indisponível neste navegador')
    this.ctx = ctx

    const AudioCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    this.audioContext = new AudioCtor()
    this.masterGain = this.audioContext.createGain()
    this.masterGain.connect(this.audioContext.destination)
    this.pool = new MediaPool(this.audioContext, this.masterGain)

    this.getSequence = getSequence
    this.assets = assets
    this.callbacks = callbacks
    this.pool.setErrorHandler((assetId, message) => {
      callbacks.onError?.(`Não foi possível reproduzir a mídia: ${message}.`)
      void assetId
    })

    this.loop = this.loop.bind(this)
  }

  get time(): number {
    return this.currentTime
  }

  get isPlaying(): boolean {
    return this.playing
  }

  async play(): Promise<void> {
    if (this.playing) return
    // Navegadores só liberam áudio depois de um gesto do usuário; retomar aqui é o
    // ponto certo, porque play() só é chamado a partir de um clique ou tecla.
    if (this.audioContext.state === 'suspended') await this.audioContext.resume()

    const sequence = this.getSequence()
    if (!sequence) return
    const duration = sequenceDuration(sequence)
    if (this.currentTime >= duration - 1e-3) this.currentTime = 0

    this.playing = true
    this.clockStart = this.audioContext.currentTime
    this.clockOffset = this.currentTime
    this.startLoop()
  }

  pause(): void {
    if (!this.playing) return
    this.playing = false
    this.pool.pauseAll()
    // Continua desenhando uma vez para o frame parado ficar correto.
    this.renderFrame(this.currentTime)
  }

  seek(time: number): void {
    const sequence = this.getSequence()
    const duration = sequence ? sequenceDuration(sequence) : 0
    this.currentTime = Math.max(0, Math.min(time, duration))
    this.clockStart = this.audioContext.currentTime
    this.clockOffset = this.currentTime
    if (!this.playing) {
      this.pool.pauseAll()
      this.renderFrame(this.currentTime)
    }
    this.callbacks.onTimeUpdate(this.currentTime)
  }

  setVolume(value: number): void {
    this.masterGain.gain.value = Math.max(0, Math.min(2, value))
  }

  /** Redesenha sem mudar o tempo — usado quando a timeline muda com o player parado. */
  refresh(): void {
    if (!this.playing) this.renderFrame(this.currentTime)
  }

  resize(width: number, height: number): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.canvas.width = Math.round(width * dpr)
    this.canvas.height = Math.round(height * dpr)
    this.canvas.style.width = `${width}px`
    this.canvas.style.height = `${height}px`
    this.refresh()
  }

  dispose(): void {
    this.playing = false
    if (this.rafId !== null) cancelAnimationFrame(this.rafId)
    this.pool.dispose()
    void this.audioContext.close()
  }

  private startLoop(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId)
    this.rafId = requestAnimationFrame(this.loop)
  }

  private loop(): void {
    if (!this.playing) {
      this.rafId = null
      return
    }
    const sequence = this.getSequence()
    if (!sequence) {
      this.rafId = requestAnimationFrame(this.loop)
      return
    }

    // O tempo vem do relógio de áudio; rAF só decide QUANDO desenhar.
    const elapsed = this.audioContext.currentTime - this.clockStart
    this.currentTime = this.clockOffset + elapsed

    const duration = sequenceDuration(sequence)
    if (this.currentTime >= duration) {
      this.currentTime = duration
      this.playing = false
      this.pool.pauseAll()
      this.renderFrame(this.currentTime)
      this.callbacks.onTimeUpdate(this.currentTime)
      this.callbacks.onEnded()
      this.rafId = null
      return
    }

    this.renderFrame(this.currentTime)

    // Reportar a cada frame faria o React re-renderizar 60 vezes por segundo. ~20 Hz é
    // suficiente para o timecode na tela e não compete com o desenho.
    if (Math.abs(this.currentTime - this.lastReported) > 0.05) {
      this.lastReported = this.currentTime
      this.callbacks.onTimeUpdate(this.currentTime)
    }

    this.rafId = requestAnimationFrame(this.loop)
  }

  private renderFrame(time: number): void {
    const sequence = this.getSequence()
    if (!sequence) return

    const { ctx, canvas } = this
    const scale = canvas.width / sequence.width

    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.fillStyle = sequence.backgroundColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.scale(scale, scale)

    const active = activeItemsAt(sequence, time)
    const activeAssets = new Set<string>()

    for (const { track, item } of active) {
      if (item.kind === 'video' || item.kind === 'image') {
        this.drawVisual(track, item, time, sequence)
        if (item.source) activeAssets.add(item.source.assetId)
      } else if (item.kind === 'text' && item.text) {
        drawText(ctx, item, sequence, time - item.start)
      } else if (item.kind === 'caption' && item.caption) {
        drawCaption(ctx, item, time, sequence)
      }

      if (item.source && (item.kind === 'audio' || item.kind === 'video')) {
        this.syncAudio(track, item, time)
        activeAssets.add(item.source.assetId)
      }
    }

    this.pool.pauseAllExcept(activeAssets)
    ctx.restore()
  }

  private drawVisual(track: Track, item: TimelineItem, time: number, sequence: Sequence): void {
    if (!item.source) return
    const source = this.assets.get(item.source.assetId)
    if (!source) return

    const media = this.pool.get(source)
    const element = media.element

    // Cada tipo de elemento diz de um jeito diferente que já tem pixels para desenhar.
    let sourceWidth: number
    let sourceHeight: number
    if (element instanceof HTMLVideoElement) {
      if (element.readyState < 2) return
      this.syncVideo(element, item, time)
      sourceWidth = element.videoWidth
      sourceHeight = element.videoHeight
    } else if (element instanceof HTMLImageElement) {
      if (!element.complete || element.naturalWidth === 0) return
      sourceWidth = element.naturalWidth
      sourceHeight = element.naturalHeight
    } else {
      return
    }
    if (!sourceWidth || !sourceHeight) return

    const local = time - item.start
    const props = evaluateItem(item, local)
    const { ctx } = this

    // Tamanho e posição saem de `layoutVisual` — a MESMA função que o compilador de
    // render usa. É o que impede o elemento gráfico de aparecer de um tamanho no
    // preview e de outro no arquivo exportado.
    const layout = layoutVisual(
      item,
      { width: sourceWidth, height: sourceHeight },
      { width: sequence.width, height: sequence.height },
      props,
    )

    // Fade in/out multiplica a opacidade, exatamente como o `fade` do FFmpeg faz sobre
    // o alfa depois do `colorchannelmixer`.
    const alpha = Math.max(0, Math.min(1, props.opacity * fadeGain(item, local)))
    if (alpha <= 0) return

    ctx.save()
    ctx.globalAlpha = alpha
    ctx.translate(layout.centerX, layout.centerY)
    if (props.rotation) ctx.rotate((props.rotation * Math.PI) / 180)
    try {
      ctx.drawImage(
        element,
        layout.crop.x,
        layout.crop.y,
        layout.crop.width,
        layout.crop.height,
        -layout.width / 2,
        -layout.height / 2,
        layout.width,
        layout.height,
      )
    } catch {
      // Frame ainda não decodificado: pular é melhor que quebrar o loop.
    }
    ctx.restore()
  }

  /**
   * Mantém o `<video>` alinhado ao tempo da timeline.
   *
   * Fora de tolerância, um seek; dentro dela, deixa correr. Fazer seek a cada frame
   * transformaria a reprodução num slideshow — seek é caro em qualquer navegador.
   */
  private syncVideo(element: HTMLVideoElement, item: TimelineItem, time: number): void {
    const target = sourceTimeAt(item, time)
    if (target === null) return

    const drift = Math.abs(element.currentTime - target)
    if (drift > DRIFT_TOLERANCE) {
      element.currentTime = target
    }

    const rate = Math.abs(item.speed) || 1
    if (Math.abs(element.playbackRate - rate) > 0.01) {
      // Fora de 0.25–4 o navegador silencia ou recusa; travar aqui evita áudio mudo
      // sem explicação.
      element.playbackRate = Math.max(0.25, Math.min(4, rate))
    }

    if (this.playing && element.paused) {
      void element.play().catch(() => {
        this.callbacks.onError?.('O navegador bloqueou a reprodução. Clique no player para liberar.')
      })
    } else if (!this.playing && !element.paused) {
      element.pause()
    }
  }

  private syncAudio(track: Track, item: TimelineItem, time: number): void {
    if (!item.source) return
    const source = this.assets.get(item.source.assetId)
    if (!source?.hasAudio) return

    const media = this.pool.get(source)
    const element = media.element

    // Vídeo já foi sincronizado no desenho; áudio puro sincroniza aqui. Imagem não tem
    // relógio nenhum.
    if (element instanceof HTMLAudioElement) {
      const target = sourceTimeAt(item, time)
      if (target !== null && Math.abs(element.currentTime - target) > DRIFT_TOLERANCE) {
        element.currentTime = target
      }
      if (this.playing && element.paused) void element.play().catch(() => undefined)
      if (!this.playing && !element.paused) element.pause()
    }

    const gain = this.pool.gainFor(item.source.assetId)
    if (!gain) return

    const local = time - item.start
    const props = evaluateItem(item, local)
    const muted = item.muted || track.muted
    const value = muted ? 0 : props.volume * track.volume * fadeGain(item, local)
    // Rampa curta: mudanças abruptas de ganho produzem estalo audível.
    gain.gain.setTargetAtTime(Math.max(0, Math.min(2, value)), this.audioContext.currentTime, 0.01)
  }
}
